import React, { useEffect, useState } from "react";
import axios from "axios";
import { Mail, User, Calendar, Search, Trash2 } from "lucide-react";

const ContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0
  });

  const token = localStorage.getItem("token");

  const fetchMessages = async (page = 1) => {
    if (!token) {
      setError("Token d'authentification manquant.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get("https://api.greenecobagsusa.com/api/contact-messages", {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        },
        params: { page }
      });

      const data = response.data;

      if (Array.isArray(data.data)) {
        setMessages(data.data);
        setFilteredMessages(data.data);
        setPagination({
          current_page: data.current_page,
          last_page: data.last_page,
          per_page: data.per_page,
          total: data.total
        });
      } else {
        setMessages([]);
        setFilteredMessages([]);
        setError("Format de réponse inattendu.");
      }
    } catch (err) {
      console.error("Erreur lors du chargement des messages :", err);
      setError("Erreur serveur ou réseau.");
      setMessages([]);
      setFilteredMessages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages(pagination.current_page);
  }, [pagination.current_page]);

  useEffect(() => {
    const term = searchTerm.toLowerCase();
    const filtered = messages.filter(
      msg =>
        msg.name.toLowerCase().includes(term) ||
        msg.email.toLowerCase().includes(term) ||
        msg.message.toLowerCase().includes(term)
    );
    setFilteredMessages(filtered);
  }, [searchTerm, messages]);

  const handleDelete = async id => {
    if (!token) return;

    try {
      await axios.delete(`https://api.greenecobagsusa.com/api/contact-messages/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const updated = messages.filter(msg => msg.id !== id);
      setMessages(updated);
      setFilteredMessages(updated);

      // Si la page devient vide après suppression, revenir à la page précédente
      if (updated.length === 0 && pagination.current_page > 1) {
        setPagination(prev => ({
          ...prev,
          current_page: prev.current_page - 1
        }));
      }
    } catch (err) {
      console.error("Erreur lors de la suppression :", err);
    }
  };

  return (
    <section className="max-w-5xl mx-auto">
      <h2 className="text-3xl font-bold text-gray-900 mb-6">📬 Messages de contact</h2>

      {/* Barre de recherche */}
      <div className="mb-8 flex items-center gap-3">
        <div className="relative w-full">
          <input
            type="text"
            placeholder="Rechercher par nom, email ou message..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          <Search className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
        </div>
      </div>

      {/* Affichage des messages */}
      {loading ? (
        <div className="text-center text-gray-500 py-12">Chargement des messages...</div>
      ) : error ? (
        <div className="text-center text-red-500 py-12">{error}</div>
      ) : filteredMessages.length > 0 ? (
        <div className="space-y-6">
          {filteredMessages.map((msg, index) => (
            <div
              key={msg.id}
              className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 animate-fade-in-up"
              style={{ animationDelay: `${index * 50}ms`, animationFillMode: "both" }}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <div className="bg-green-500 text-white rounded-full p-3">
                    <User className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">{msg.name}</h3>
                    <p className="text-sm text-gray-500 flex items-center">
                      <Mail className="w-4 h-4 mr-1" />
                      {msg.email}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-4 sm:mt-0">
                  <div className="text-sm text-gray-500 flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    {msg.created_at
                      ? new Date(msg.created_at).toLocaleString("fr-FR", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit"
                        })
                      : "Date inconnue"}
                  </div>
                  <button
                    onClick={() => handleDelete(msg.id)}
                    className="text-red-500 hover:text-red-700 transition"
                    title="Supprimer le message"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <p className="text-gray-700 whitespace-pre-line leading-relaxed">{msg.message}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center text-gray-500 py-12">Aucun message trouvé.</div>
      )}

      {/* Pagination */}
      {pagination.last_page > 1 && (
        <div className="flex justify-center mt-8 flex-wrap gap-2">
          {Array.from({ length: pagination.last_page }, (_, i) => (
            <button
              key={i + 1}
              onClick={() =>
                setPagination(prev => ({
                  ...prev,
                  current_page: i + 1
                }))
              }
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all duration-200 transform hover:scale-105 ${
                pagination.current_page === i + 1
                  ? "bg-green-600 text-white border-green-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </section>
  );
};

export default ContactMessages;
